import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";

const MY_SUBSCRIPTION_STATUS = gql`
  query MySubscriptionStatusForBanner {
    mySubscriptionStatus {
      hasActiveSubscription
    }
  }
`;

const CREATE_MY_CHECKOUT_SESSION = gql`
  mutation CreateMyCheckoutSessionForBanner($returnUrl: String!) {
    createMyCheckoutSession(returnUrl: $returnUrl)
  }
`;

// Compact CTA shown at the top of the Events tab for users who don't
// currently have an active Stripe subscription. Three states:
//   - sex unanswered -> "finish your profile" prompt (opens the
//     t-shirt/sex modal via onNeedInfo); the subscribe button stays
//     hidden until the question is answered, since the answer decides
//     whether subscribing even applies.
//   - sex === "female" -> nothing (women-free policy).
//   - sex === "male"  -> Subscribe button (Stripe Checkout).
// Auto-hides entirely for active subscribers.
//
// On click, opens a Stripe Checkout Session via the same graphql
// mutation that MySubscriptionSection uses; success bounces back to
// the page that opened the banner with ?subscribed=1.
const SubscribeBanner = ({ userInfo, onNeedInfo }) => {
  const { data, loading } = useQuery(MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: "network-only",
  });
  const [createCheckout, { loading: checkoutLoading }] = useMutation(
    CREATE_MY_CHECKOUT_SESSION
  );
  const [error, setError] = useState("");

  if (loading || !data) return null;
  if (data.mySubscriptionStatus?.hasActiveSubscription) return null;
  // Women have free access today; don't push them to subscribe.
  const sex = String(userInfo?.sex || "").trim().toLowerCase();
  if (sex === "female") return null;

  // Not answered yet (legacy rows may carry "default") — prompt for
  // the profile questions instead of showing a subscribe button.
  if (sex !== "male") {
    return (
      <div
        style={{
          marginBottom: 12,
          padding: "12px 16px",
          background: "#eef4fb",
          border: "1px solid #bcd4ef",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          <strong style={{ color: "#1d3d5f", display: "block" }}>
            Finish setting up your profile
          </strong>
          <span style={{ color: "#3a5c80", fontSize: 13 }}>
            A couple quick questions unlock your membership options.
          </span>
        </div>
        <button
          type="button"
          onClick={() => onNeedInfo && onNeedInfo()}
          style={{
            padding: "10px 18px",
            background: "#1f6feb",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Get started
        </button>
      </div>
    );
  }

  const goCheckout = async () => {
    setError("");
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}`
        : "https://activebeaches.com/dashboard";
    try {
      const { data: out } = await createCheckout({ variables: { returnUrl } });
      const url = out?.createMyCheckoutSession;
      if (url) {
        window.location.href = url;
      } else {
        setError("Stripe returned no checkout URL.");
      }
    } catch (err) {
      setError(err.message || "Couldn't start checkout.");
    }
  };

  return (
    <div
      style={{
        marginBottom: 12,
        padding: "12px 16px",
        background: "#eef7ee",
        border: "1px solid #b9dcb5",
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <strong style={{ color: "#1f4f1d", display: "block" }}>
          Subscribe to unlock event details
        </strong>
        <span style={{ color: "#3a6c36", fontSize: 13 }}>
          See private locations, RSVPs, and our GroupMe chat.
        </span>
      </div>
      <button
        type="button"
        onClick={goCheckout}
        disabled={checkoutLoading}
        style={{
          padding: "10px 18px",
          background: "#1f7a3a",
          color: "white",
          border: "none",
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 600,
          cursor: checkoutLoading ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {checkoutLoading ? "Opening Stripe…" : "Subscribe"}
      </button>
      {error && (
        <span
          style={{
            color: "#b00020",
            fontSize: 12,
            width: "100%",
            marginTop: 4,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default SubscribeBanner;
